import { navigate } from 'gatsby';
import { trackEvent } from '../components/Analytics';

// Add wiggle animation styles
const wiggleStyles = `
  @keyframes wiggle {
    0% { transform: rotate(0deg); }
    25% { transform: rotate(-2deg); }
    50% { transform: rotate(2deg); }
    75% { transform: rotate(-2deg); }
    100% { transform: rotate(0deg); }
  }
  @keyframes wiggleMobile {
    0% { transform: rotate(0deg); }
    16.6% { transform: rotate(-2deg); }
    33.3% { transform: rotate(2deg); }
    50% { transform: rotate(-2deg); }
    66.6% { transform: rotate(2deg); }
    83.3% { transform: rotate(-2deg); }
    100% { transform: rotate(0deg); }
  }
  .wiggle {
    animation: wiggle 0.4s ease-in-out;
    transform-origin: center center;
  }
  .wiggle-mobile {
    animation: wiggleMobile 0.6s ease-in-out;
    transform-origin: center center;
  }
`;

// Ensure styles are added to document
let stylesAdded = false;
const ensureWiggleStyles = () => {
  if (!stylesAdded && typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = wiggleStyles;
    document.head.appendChild(styleSheet);
    stylesAdded = true;
  }
};

/**
 * Checks if an element is in the viewport
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} Whether the element is in the viewport
 */
const isInViewport = element => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;

  // Check if element is visible in viewport (with some padding)
  return (
    rect.top >= -100 && // Allow 100px above viewport
    rect.bottom <= windowHeight + 100 && // Allow 100px below viewport
    rect.left >= 0 &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Scrolls to the email input on the home page and focuses it with wiggle animation
 * @param {boolean} isMobile - Whether this is triggered from mobile menu
 * @param {function} onCloseMenu - Optional callback to close mobile menu
 * @param {string} source - Source of the scroll action for tracking
 * @param {object} config - Configuration for targeting specific page/input
 * @param {string} config.targetPath - Path to navigate to (default: '/')
 * @param {string} config.emailSelector - Selector for email input (default: 'input[type="email"]')
 * @param {boolean} config.stayOnCurrentPage - Whether to stay on current page (default: false)
 * @param {string} config.scrollTarget - Selector for element to scroll to (default: uses emailSelector)
 * @param {number} config.scrollOffset - Offset in pixels from top of scroll target (default: 100)
 */
export const scrollToEmailForm = (
  isMobile = false,
  onCloseMenu = null,
  source = 'unknown',
  config = {}
) => {
  const {
    targetPath = '/',
    emailSelector = 'input[type="email"]',
    stayOnCurrentPage = false,
    scrollTarget = null,
    scrollOffset = 100,
  } = config;

  // Track the scroll-to-email attempt
  trackEvent('scroll', {
    action: 'attempted',
    target: 'email',
    source: source,
    is_mobile: isMobile,
    pathname:
      typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    target_path: targetPath,
    stay_on_page: stayOnCurrentPage,
    scroll_target: scrollTarget || emailSelector,
    scroll_offset: scrollOffset,
  });

  // Close mobile menu if callback provided
  if (onCloseMenu) {
    onCloseMenu();
  }

  // Ensure wiggle styles are available
  ensureWiggleStyles();

  if (typeof window === 'undefined') return;

  const currentPath = window.location.pathname;
  const shouldStayOnPage = stayOnCurrentPage || currentPath === targetPath;

  if (shouldStayOnPage) {
    // Stay on current page - scroll to email input and wiggle
    const emailInput = document.querySelector(emailSelector);

    if (emailInput) {
      // Determine the scroll target - either specified target or email input's section
      const scrollTargetElement = scrollTarget
        ? document.querySelector(scrollTarget)
        : emailInput.closest('section') || emailInput;

      const isFormVisible = isInViewport(scrollTargetElement);

      if (isFormVisible) {
        // Form is already visible, just focus and wiggle without scrolling
        if (isMobile) {
          // On mobile, only shake the input without focusing
          emailInput.classList.add('wiggle-mobile');
          setTimeout(() => {
            emailInput.classList.remove('wiggle-mobile');
          }, 600);
        } else {
          // On desktop, focus and shake
          emailInput.focus();
          emailInput.classList.add('wiggle');
          setTimeout(() => {
            emailInput.classList.remove('wiggle');
          }, 400);
        }

        // Track successful interaction
        trackEvent('scroll', {
          action: 'completed',
          target: 'email',
          source: source,
          is_mobile: isMobile,
          action_detail: 'wiggle_only',
          target_path: targetPath,
          scroll_target: scrollTarget || emailSelector,
        });

        return;
      }

      // Form not visible, proceed with scroll
      // Get the current scroll position and target position
      const currentScroll = window.pageYOffset;
      const targetPosition = scrollTargetElement.offsetTop - scrollOffset;

      // Smooth scroll with easing
      const startTime = performance.now();
      const duration = 800; // 800ms for smooth scroll

      const easeInOutCubic = t => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = currentTime => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        const newPosition =
          currentScroll + (targetPosition - currentScroll) * easedProgress;
        window.scrollTo(0, newPosition);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          // Scroll complete, now focus and wiggle
          setTimeout(() => {
            if (isMobile) {
              // On mobile, only shake the input without focusing
              emailInput.classList.add('wiggle-mobile');
              setTimeout(() => {
                emailInput.classList.remove('wiggle-mobile');
              }, 600);
            } else {
              // On desktop, focus and shake
              emailInput.focus();
              emailInput.classList.add('wiggle');
              setTimeout(() => {
                emailInput.classList.remove('wiggle');
              }, 400);
            }

            // Track successful scroll and interaction
            trackEvent('scroll', {
              action: 'completed',
              target: 'email',
              source: source,
              is_mobile: isMobile,
              action_detail: 'scroll_and_wiggle',
              target_path: targetPath,
              scroll_target: scrollTarget || emailSelector,
            });
          }, 100);
        }
      };

      requestAnimationFrame(animateScroll);
    } else {
      // Email input not found - no tracking needed for failures
    }
  } else {
    // Navigate to target page with state to trigger focus/wiggle
    navigate(targetPath, {
      state: {
        shouldFocusEmail: !isMobile, // Only focus on desktop
        isMobile,
        scrollSource: source, // Pass source for tracking after navigation
        emailSelector, // Pass selector for targeting specific input
        scrollTarget, // Pass scroll target for navigation
        scrollOffset, // Pass scroll offset for navigation
      },
    });
  }
};

/**
 * Detects if the current device is mobile
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return (
    /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
    window.innerWidth <= 768 ||
    'ontouchstart' in window
  );
};

/**
 * Higher-order function that returns a click handler for scroll-to-email functionality
 * @param {boolean} isMobile - Whether this is for mobile usage
 * @param {function} onCloseMenu - Optional callback to close mobile menu
 * @param {string} source - Source of the scroll action for tracking
 * @returns {function} Click handler function
 */
export const createScrollToEmailHandler = (
  isMobile = false,
  onCloseMenu = null,
  source = 'unknown'
) => {
  return e => {
    e.preventDefault();
    scrollToEmailForm(isMobile, onCloseMenu, source);
  };
};
